/**
 * use-route-loading.ts — global "page is loading" state for the H5 shell.
 *
 * Every H5 route is lazy-loaded, so a navigation can take a visible moment to
 * fetch its chunk (and the auth guard can add a round trip on top). This module
 * turns that window into one reactive flag the app shell renders as the
 * SoulSpace splash, instead of each page inventing its own spinner.
 *
 * The flag starts `true` so the splash covers the gap between Vue mounting and
 * the first route resolving. Later navigations only flip it after a short delay,
 * so moving between already-cached pages does not flash the splash for a frame.
 */
import { readonly, ref } from "vue";
import type { Router } from "vue-router";

/** How long a navigation may run before the splash appears. */
const SHOW_DELAY_MS = 120;

const routeLoading = ref(true);
let showTimer: ReturnType<typeof setTimeout> | undefined;

function beginNavigation(): void {
  if (showTimer !== undefined) {
    clearTimeout(showTimer);
  }
  showTimer = setTimeout(() => {
    routeLoading.value = true;
  }, SHOW_DELAY_MS);
}

function endNavigation(): void {
  if (showTimer !== undefined) {
    clearTimeout(showTimer);
    showTimer = undefined;
  }
  routeLoading.value = false;
}

/** Hooks the splash state into the router. Called once from main.ts. */
export function setupRouteLoading(router: Router): void {
  router.beforeEach(beginNavigation);
  router.afterEach(endNavigation);
  // A failed chunk fetch or a guard throw must not leave the splash up forever;
  // the router renders its error state and the flag has to follow.
  router.onError(endNavigation);
}

export function useRouteLoading() {
  return readonly(routeLoading);
}
